/**
 * Super Admin Issuer Settings Page - 발행처 정보 관리
 */

import { useState, useEffect } from 'react';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Save, Building2 } from 'lucide-react';
import { AdminFire } from '@/services/SuperAdminFirebase';

interface IssuerInfo {
    name: string;
    registrationNumber: string;
    address: string;
    phone: string;
    email: string;
    bankInfo: string;
}

const DEFAULT_ISSUER: IssuerInfo = {
    name: 'Sunbi Soft',
    registrationNumber: 'T0000000000000',
    address: '東京都',
    phone: '',
    email: 'billing@sunbisoft.com',
    bankInfo: '',
};

export default function SuperAdminIssuerPage() {
    const { i18n } = useLingui();
    const [issuer, setIssuer] = useState<IssuerInfo>(DEFAULT_ISSUER);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const store = AdminFire.store;
            const docRef = doc(store, 'admin', 'settings');
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.issuer) {
                    setIssuer({ ...DEFAULT_ISSUER, ...data.issuer });
                }
            }
        } catch (e) {
            console.error('Failed to load settings:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const store = AdminFire.store;
            const docRef = doc(store, 'admin', 'settings');
            await setDoc(docRef, { issuer }, { merge: true });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save settings:', e);
            alert(t(i18n)`Failed to save settings`);
        } finally {
            setSaving(false);
        }
    };

    const updateIssuer = (field: keyof IssuerInfo, value: string) => {
        setIssuer(prev => ({ ...prev, [field]: value }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
                <Building2 className="h-8 w-8" />
                <div>
                    <h1 className="text-2xl font-bold"><Trans>Issuer Info</Trans></h1>
                    <p className="text-muted-foreground"><Trans>Issuer info displayed on invoices & receipts</Trans></p>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle><Trans>Issuer Info</Trans></CardTitle>
                    <CardDescription>
                        <Trans>Set the issuer info for invoices & receipts. Please also register the qualified invoice issuer registration number.</Trans>
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="issuer-name"><Trans>Company Name</Trans></Label>
                            <Input
                                id="issuer-name"
                                value={issuer.name}
                                onChange={(e) => updateIssuer('name', e.target.value)}
                                placeholder="株式会社○○"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="issuer-registration"><Trans>Registration No.</Trans></Label>
                            <Input
                                id="issuer-registration"
                                value={issuer.registrationNumber}
                                onChange={(e) => updateIssuer('registrationNumber', e.target.value)}
                                placeholder="T0000000000000"
                            />
                            <p className="text-xs text-muted-foreground">
                                <Trans>Qualified Invoice Issuer Registration No.</Trans>
                            </p>
                        </div>

                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="issuer-address"><Trans>Address</Trans></Label>
                            <Input
                                id="issuer-address"
                                value={issuer.address}
                                onChange={(e) => updateIssuer('address', e.target.value)}
                                placeholder="東京都渋谷区..."
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="issuer-phone"><Trans>Phone Number</Trans></Label>
                            <Input
                                id="issuer-phone"
                                value={issuer.phone}
                                onChange={(e) => updateIssuer('phone', e.target.value)}
                                placeholder="03-1234-5678"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="issuer-email"><Trans>Email Address</Trans></Label>
                            <Input
                                id="issuer-email"
                                type="email"
                                value={issuer.email}
                                onChange={(e) => updateIssuer('email', e.target.value)}
                                placeholder="billing@example.com"
                            />
                        </div>

                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="issuer-bank"><Trans>Bank Transfer Info</Trans></Label>
                            <Textarea
                                id="issuer-bank"
                                value={issuer.bankInfo}
                                onChange={(e) => updateIssuer('bankInfo', e.target.value)}
                                placeholder="○○銀行 ○○支店 普通 1234567 カ）○○○○"
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t">
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <span className="animate-spin mr-2">⏳</span>
                            ) : (
                                <Save className="mr-2 h-4 w-4" />
                            )}
                            {saved ? <Trans>Saved</Trans> : <Trans>Save</Trans>}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
